import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useWebhookStatus } from '@/hooks/useWebhookStatus';
import { Webhook, Copy, CheckCircle, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const WebhookConfiguration = () => {
  const { 
    webhookUrl, 
    lastEventDate, 
    lastEventType, 
    lastEventStatus,
    isWorking, 
    isTesting,
    testWebhook,
    refetchLogs 
  } = useWebhookStatus();

  const copyWebhookUrl = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl);
      toast({
        title: "URL copiada!",
        description: "A URL do webhook foi copiada para a área de transferência.",
      });
    }
  };

  const handleTest = async () => {
    const result = await testWebhook();
    if (result.success) {
      toast({
        title: "Verificação atualizada",
        description: "Status do webhook verificado com sucesso.",
      });
    }
  };

  const requiredEvents = [
    'checkout.session.completed',
    'customer.subscription.created',
    'customer.subscription.deleted',
    'payment_intent.succeeded',
    'charge.refunded'
  ];

  if (!webhookUrl) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Configure a integração com Stripe primeiro para habilitar o webhook.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Webhook className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Webhook do Stripe</CardTitle>
              <CardDescription>
                Configure para sincronização automática de pagamentos
              </CardDescription>
            </div>
          </div>
          <Badge variant={isWorking ? "default" : "secondary"}>
            {isWorking ? (
              <><CheckCircle className="h-3 w-3 mr-1" /> Funcionando</>
            ) : (
              <><AlertCircle className="h-3 w-3 mr-1" /> Não configurado</>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Webhook URL */}
        <div className="space-y-2">
          <label className="text-sm font-medium">URL do Webhook</label>
          <div className="flex gap-2">
            <div className="flex-1 p-3 bg-muted rounded-md font-mono text-xs break-all">
              {webhookUrl}
            </div>
            <Button 
              size="icon" 
              variant="outline"
              onClick={copyWebhookUrl}
              title="Copiar URL"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Status and Last Event */}
        {lastEventDate && (
          <div className="flex items-center justify-between p-3 bg-muted rounded-md">
            <div className="space-y-1">
              <p className="text-sm font-medium">Último evento recebido</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{lastEventType}</span>
                <span>•</span>
                <span>{format(new Date(lastEventDate), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                <Badge variant={lastEventStatus === 'success' ? 'default' : 'destructive'} className="text-xs">
                  {lastEventStatus}
                </Badge>
              </div>
            </div>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => refetchLogs()}
              disabled={isTesting}
            >
              <RefreshCw className={`h-4 w-4 ${isTesting ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        )}

        {/* Instructions */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="instructions">
            <AccordionTrigger>
              <span className="text-sm font-medium">Como configurar o webhook no Stripe</span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                <li className="space-y-2">
                  <span>Acesse o Dashboard do Stripe</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="ml-2"
                    onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Abrir Stripe
                  </Button>
                </li>
                <li>Navegue para <strong>Desenvolvedores → Webhooks</strong></li>
                <li>Clique em <strong>Adicionar endpoint</strong></li>
                <li>Cole a URL do webhook acima no campo "Endpoint URL"</li>
                <li>Na seção "Eventos a enviar", selecione os seguintes eventos:</li>
              </ol>

              {/* Required Events List */}
              <div className="pl-8 space-y-1">
                {requiredEvents.map(event => (
                  <div key={event} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    <code className="bg-muted px-2 py-0.5 rounded">{event}</code>
                  </div>
                ))}
              </div>

              <ol start={6} className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                <li>Clique em <strong>Adicionar endpoint</strong> para salvar</li>
                <li>Após salvar, copie a "Chave de assinatura do webhook" (webhook signing secret) se necessário</li>
              </ol>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <strong>Importante:</strong> Configure o webhook tanto no modo teste quanto no modo produção do Stripe. Cada ambiente tem sua própria configuração de webhooks.
                </AlertDescription>
              </Alert>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Test Button */}
        <div className="flex gap-2 pt-2">
          <Button 
            variant="outline" 
            onClick={handleTest}
            disabled={isTesting}
            className="flex-1"
          >
            {isTesting ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Verificando...</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-2" /> Verificar Status</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
